import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { LOGIN_SCOPES, msalInstance } from '@/authConfig'
import { AuthenticationResult, EventType, AccountInfo, SilentRequest } from '@azure/msal-browser'
import { acquireAndProcessIdTokenClaims, getIsPWAdmin, processIdTokenClaims } from '@/azure/token-utils'
import { useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'

export const useAuthStore = defineStore('auth', () => {
  // State
  const activeAccount = ref<AccountInfo | null>(msalInstance.getActiveAccount())
  const accounts = ref<AccountInfo[]>(msalInstance.getAllAccounts())
  const idTokenClaims = ref<Record<string, any> | null>(null)

  // Helper to update idTokenClaims
  const updateIdTokenClaims = async (account: AccountInfo | null) => {
    try {
      idTokenClaims.value = await acquireAndProcessIdTokenClaims(msalInstance)
    } catch (e) {
      idTokenClaims.value = null
    }
  }

  // On store init, try to populate idTokenClaims
  if (activeAccount.value) {
    updateIdTokenClaims(activeAccount.value)
  }

  // Listen to MSAL events
  msalInstance.addEventCallback(async (event) => {
    // log the event for debugging
    console.log('MSAL Event:', event)
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult
      msalInstance.setActiveAccount(payload.account)
      activeAccount.value = payload.account
      accounts.value = msalInstance.getAllAccounts()
      await updateIdTokenClaims(payload.account)
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      msalInstance.setActiveAccount(null)
      activeAccount.value = null
      accounts.value = msalInstance.getAllAccounts()
      idTokenClaims.value = null
    }
    if (event.eventType === EventType.HANDLE_REDIRECT_END) {
      const _activeAccount = msalInstance.getActiveAccount()

      // log the active account after redirect
      console.log('Active account after redirect:', _activeAccount)

      // After redirect, update accounts and active account
      activeAccount.value = _activeAccount
      accounts.value = msalInstance.getAllAccounts()

      // Update idTokenClaims after redirect
      await updateIdTokenClaims(_activeAccount)
    }
    // Handle acquireTokenFailure and other auth errors: auto-logout on session-related errors
    if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE && event.error) {
      // List of error codes that require logout
      const logoutErrorCodes = [
        'interaction_required',
        'login_required',
        'consent_required',
        'user_password_expired',
        'token_renewal_error',
        'invalid_grant',
        'invalid_request',
      ]
      if (
        event.error instanceof (await import('@azure/msal-browser')).AuthError &&
        logoutErrorCodes.includes(event.error.errorCode)
      ) {
        await msalInstance.logoutRedirect()
      }
    }
  })

  // Computed
  const isAuthenticated = computed(() => !!activeAccount.value)
  const isPWAdmin = computed(() => getIsPWAdmin(idTokenClaims.value))

  return {
    msalInstance,
    activeAccount,
    accounts,
    idTokenClaims,
    isAuthenticated,
    isPWAdmin,
  }
})
